import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { store } from '../../redux/oldStore';
import { ServerResponse } from '../../utils/Server';
import { FormatDate } from '../../utils/Tools';
import DangerousInnerHtml from '../ParticipantVideo/DangerousInnerHtml';
import './MessageView.css';

interface IMessageView {
  publicId?: string;
  type?: string;
  subject?: string;
  body?: string;
  sentAtUtc?: string;
  readAtUtc?: string;
}

/**
 * Thin full-message viewer for SMS / short links (replaces Message_Index.cshtml).
 * Users open this to read the full text when the SMS was truncated.
 */
const MessageViewPage = () => {
  const { id } = useParams<{ id: string }>();
  const [message, setMessage] = useState<IMessageView | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (!id) {
      setNotFound(true);
      setLoading(false);
      return;
    }

    store.server
      .postApi<ServerResponse<IMessageView>>(`../Message/ViewData`, { id })
      .then((x) => {
        if (x.Success && x.Value) {
          setMessage(x.Value);
        } else {
          setNotFound(true);
          if (x.Message) toast.error(x.Message, { autoClose: false });
        }
      })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return (
      <div className="message-view-shell" data-testid="message-view-page">
        <div className="message-view-card">
          <p className="text-muted">Loading message…</p>
        </div>
      </div>
    );
  }

  if (notFound || !message) {
    return (
      <div className="message-view-shell" data-testid="message-view-page">
        <div className="message-view-card">
          <h2>Message not found</h2>
          <p>Sorry, we could not find this message.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="message-view-shell" data-testid="message-view-page">
      <div className="message-view-card">
        <div className="message-view-subject">
          <DangerousInnerHtml customText={message.subject || 'Message'} />
        </div>
        {message.sentAtUtc && (
          <div className="message-view-meta text-muted">Sent: {FormatDate(message.sentAtUtc, 'date-time')}</div>
        )}
        <div className="message-view-body">
          <DangerousInnerHtml customText={message.body || ''} />
        </div>
      </div>
    </div>
  );
};

export default MessageViewPage;
